import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import noop from 'lodash/noop';
import sum from 'lodash/sum';
import pluralize from 'pluralize';

import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  GridLegacy as Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import NumberFormatCustom from '~/components/Common/ReactNumberFormat/NumberFormatCustom';
import Spinner from '~/components/Common/Spinner';
import { RefundMetadata } from '~/components/Refund/types';

import { ReservationRate } from '~/services/ReservationService';
import { formatDateWeekDayShortMonth, generateDateRange } from '~/services/TimeService';

import currencyFormatter from '~/utils/currencyFormatter';

import { getCurrencySymbol } from '../../Utils/RefundUtils';
import {
  BASE_PACKAGE_TYPE,
  DEFAULT_SURCHARGE_TYPES,
  DisabledRefundTypes,
  ENABLE_CURRENCY_CONVERSION,
  EXTRA_GUEST_TYPE,
  NightPrices,
  PEAK_PERIOD_TYPE,
  REVENUE_MANAGEMENT_TYPE,
  SurchargePrices,
  hasAvailableSurchargeTypes,
  isSurchargeTypeAvailable,
  loadDisabledRefundTypes,
  processReservationSurcharges,
} from '../../Utils/SurchargeDataHook';

interface RefundCalculation {
  packageRefund: number;
  surchargeRefund: number;
  totalRefund: number;
  selectedNights: Array<string>;
}

// Custom hook for refund calculations with surcharge types support
function useRefundCalculations(
  refundAmountsPerNight: { [key: string]: NightPrices },
  maxRefundAmount: number,
  maxSurchargeRefundAmount: number,
  selectedSurchargeTypes: Array<string>,
) {
  const calculateRefundAmounts = useCallback(
    (selectedNights: Set<string>): RefundCalculation => {
      const selectedPrices = Array.from(selectedNights).map((date) => refundAmountsPerNight[date]);

      let packageRefund = 0;
      let surchargeRefund = 0;

      selectedPrices.forEach((prices) => {
        // Always include base package if selected
        if (selectedSurchargeTypes.includes(BASE_PACKAGE_TYPE)) {
          packageRefund += prices?.packagePrice || 0;
        }

        // Include surcharges based on selection
        if (prices?.surcharges) {
          if (selectedSurchargeTypes.includes(PEAK_PERIOD_TYPE)) {
            surchargeRefund += prices.surcharges.peak_period_surcharge_sell_amount || 0;
          }
          if (selectedSurchargeTypes.includes(EXTRA_GUEST_TYPE)) {
            surchargeRefund += prices.surcharges.extra_guest_surcharge_sell_amount || 0;
          }
          if (selectedSurchargeTypes.includes(REVENUE_MANAGEMENT_TYPE)) {
            surchargeRefund += prices.surcharges.surcharge_internal_sell_amount || 0;
          }
        }
      });
      // nightly rate (base package price) refund at maximum allowed
      packageRefund = Math.min(packageRefund, maxRefundAmount);

      // Surcharge refund at maximum allowed
      surchargeRefund = Math.min(surchargeRefund, maxSurchargeRefundAmount);

      return {
        packageRefund,
        surchargeRefund,
        totalRefund: packageRefund + surchargeRefund,
        selectedNights: Array.from(selectedNights),
      };
    },
    [refundAmountsPerNight, maxRefundAmount, maxSurchargeRefundAmount, selectedSurchargeTypes],
  );

  return { calculateRefundAmounts };
}

// Simplified hook using direct function call
function useNightPricesData(
  reservationRates: Array<ReservationRate> | undefined,
  refundMetadata: RefundMetadata,
  dateRange: Array<Date>,
  itemId: string | undefined,
) {
  const [disabledRefundTypes, setDisabledRefundTypes] = useState<{ [key: string]: DisabledRefundTypes }>({});
  const [loading, setLoading] = useState<Utils.FetchingState>('idle');
  const [reservationPriceMap, setReservationPriceMap] = useState<{ [key: string]: NightPrices } | null>(null);
  const [reservationSurcharges, setReservationSurcharges] = useState<SurchargePrices | null>(null);

  // Load disabled refund types
  useEffect(() => {
    async function loadDisabledTypes() {
      setLoading('loading');
      try {
        let disabledTypes: { [key: string]: DisabledRefundTypes } = {};
        if (itemId && refundMetadata.id_orders) {
          disabledTypes = await loadDisabledRefundTypes(itemId, refundMetadata.id_orders);
        }
        setDisabledRefundTypes(disabledTypes);
        setLoading('success');
      } catch (error) {
        setLoading('failed');
        console.error('Error loading disabled refund types:', error);
      }
    }

    loadDisabledTypes();
  }, [itemId, refundMetadata.id_orders]);

  // Process reservation rates when data is ready
  useEffect(() => {
    async function processRates() {
      if (!reservationRates?.length || loading !== 'success') {
        return;
      }

      try {
        // Call the direct function instead of the hook
        // Pass empty set for selectedNightsForCalculation since we want price map for ALL nights
        const { priceMap, availableSurcharges } = await processReservationSurcharges(
          reservationRates,
          new Set(), // Empty set = get price map for all nights, skip selected calculations
          [...DEFAULT_SURCHARGE_TYPES],
          disabledRefundTypes,
          ENABLE_CURRENCY_CONVERSION,
        );

        setReservationPriceMap(priceMap);
        setReservationSurcharges(availableSurcharges);
      } catch (error) {
        console.error('Error processing reservation rates:', error);
      }
    }

    processRates();
  }, [reservationRates, loading, disabledRefundTypes]);

  // Final processed data
  const processedData = useMemo(() => {
    const hasReservationRates = !!reservationRates?.length;

    if (loading === 'loading' || (hasReservationRates && (!reservationPriceMap || !reservationSurcharges))) {
      return {
        refundAmountsPerNight: {},
        availableSurcharges: {
          extra_guest_surcharge_sell_amount: 0,
          peak_period_surcharge_sell_amount: 0,
          surcharge_internal_sell_amount: 0,
        },
        finalLoading: 'loading' as Utils.FetchingState,
      };
    }

    if (loading === 'failed') {
      return {
        refundAmountsPerNight: {},
        availableSurcharges: {
          extra_guest_surcharge_sell_amount: 0,
          peak_period_surcharge_sell_amount: 0,
          surcharge_internal_sell_amount: 0,
        },
        finalLoading: 'failed' as Utils.FetchingState,
      };
    }

    let priceMap: { [key: string]: NightPrices } = {};
    let surchargesMap: SurchargePrices = {
      extra_guest_surcharge_sell_amount: 0,
      peak_period_surcharge_sell_amount: 0,
      surcharge_internal_sell_amount: 0,
    };

    if (hasReservationRates && reservationPriceMap && reservationSurcharges) {
      // Use processed data from unified hook (with currency conversion)
      priceMap = reservationPriceMap;
      surchargesMap = reservationSurcharges;
    }

    return {
      refundAmountsPerNight: priceMap,
      availableSurcharges: surchargesMap,
      finalLoading: 'success' as Utils.FetchingState,
    };
  }, [reservationRates, reservationPriceMap, reservationSurcharges, loading]);

  return {
    refundAmountsPerNight: processedData.refundAmountsPerNight,
    loading: processedData.finalLoading,
    disabledRefundTypes,
    availableSurcharges: processedData.availableSurcharges,
  };
}

interface Props {
  refundMetadata: RefundMetadata;
  checkInDate: string;
  checkOutDate: string;
  currencyCode: string;
  formRef: any;
  handleLateChangeOfMindFieldChange: any;
  reservationId?: string;
  maxRefundAmount: number;
  maxSurchargeRefundAmount: number;
  selectedNights?: Set<string>;
  setSelectedNights?: (dates: Set<string>) => void;
  surchargeRefundedToCustomer?: number;
  setSurchargeRefundedToCustomer?: (amount: number) => void;
  itemId?: string;
  reservationRates?: Array<ReservationRate>;
  disableCustomAmount?: boolean;
  selectedSurchargeTypes?: Array<string>;
  onSelectedSurchargeTypesChange: (types: Array<string>) => void;
}

export default function SelectNights({
  refundMetadata,
  checkInDate,
  checkOutDate,
  currencyCode,
  formRef,
  handleLateChangeOfMindFieldChange,
  maxRefundAmount,
  maxSurchargeRefundAmount = 0,
  selectedNights = new Set<string>(),
  setSelectedNights = noop,
  surchargeRefundedToCustomer,
  setSurchargeRefundedToCustomer = noop,
  itemId,
  reservationRates,
  disableCustomAmount,
  selectedSurchargeTypes = [],
  onSelectedSurchargeTypesChange,
}: Props) {
  // Use useMemo to prevent dateRange from being recreated on every render
  const dateRange = useMemo(
    () => generateDateRange(new Date(checkInDate), new Date(checkOutDate)),
    [checkInDate, checkOutDate],
  );

  const { refundAmountsPerNight, loading, disabledRefundTypes, availableSurcharges } = useNightPricesData(
    reservationRates,
    refundMetadata,
    dateRange,
    itemId,
  );

  // Use parent's state directly instead of the problematic useNightSelection hook
  const selectedNightsState = selectedNights;
  const selectNights = setSelectedNights;

  const { calculateRefundAmounts } = useRefundCalculations(
    refundAmountsPerNight,
    maxRefundAmount,
    maxSurchargeRefundAmount,
    selectedSurchargeTypes,
  );

  const [refundToCustomer, setRefundToCustomer] = useState(0);
  // Fallback nightly price if a specific date key is missing or invalid
  const fallbackNightlyPrice = useMemo(() => {
    const rawAmount =
      (refundMetadata.item_metadata &&
        (refundMetadata.item_metadata.accounting_amount ?? refundMetadata.item_metadata.cash_amount)) ??
      0;
    const numericAmount = typeof rawAmount === 'string' ? parseFloat(rawAmount) : Number(rawAmount);
    const safeAmount = Number.isFinite(numericAmount) ? numericAmount : 0;
    const nightsSum = Number(refundMetadata?.nights ?? 0) + Number(refundMetadata?.extra_nights ?? 0);
    let totalNights = Number(refundMetadata?.total_nights ?? nightsSum);
    if (!Number.isFinite(totalNights) || totalNights <= 0) {
      totalNights = dateRange.length > 0 ? dateRange.length : 1;
    }
    const avg = safeAmount / totalNights;
    return Number.isFinite(avg) ? avg : 0;
  }, [refundMetadata, dateRange]);

  // Dynamically determine available surcharge types based on reservation rates
  const availableSurchargeTypes = useMemo(() => {
    const types = [];

    // Helper function to check if a surcharge type has any refundable nights
    const hasRefundableNights = (
      checkOriginalAmount: (nightPrices: NightPrices) => number,
      checkDisabledStatus: (disabledType: DisabledRefundTypes | undefined) => boolean,
    ) => {
      return dateRange.some((date) => {
        const dateKey = date.toISOString();
        const disabledType = disabledRefundTypes[dateKey];
        const nightPrices = refundAmountsPerNight[dateKey];

        if (!nightPrices) {
          return false;
        }

        // Has original amount and not disabled
        const hasOriginalAmount = checkOriginalAmount(nightPrices) > 0;
        const isNotDisabled = !checkDisabledStatus(disabledType);

        return hasOriginalAmount && isNotDisabled;
      });
    };

    // Check base package availability
    const hasAvailableBasePackage = hasRefundableNights(
      (nightPrices) => nightPrices.originalPackagePrice ?? nightPrices.packagePrice,
      (disabledType) => disabledType?.nightly_rate ?? false,
    );

    if (hasAvailableBasePackage) {
      types.push(BASE_PACKAGE_TYPE);
    }

    // Check peak period availability
    const hasAvailablePeakPeriod = hasRefundableNights(
      (nightPrices) =>
        nightPrices.originalSurcharges?.peak_period_surcharge_sell_amount ??
        nightPrices.surcharges?.peak_period_surcharge_sell_amount ??
        0,
      (disabledType) => disabledType?.peak_period_surcharge ?? false,
    );

    if (hasAvailablePeakPeriod) {
      types.push(PEAK_PERIOD_TYPE);
    }

    // Check extra guest availability
    const hasAvailableExtraGuest = hasRefundableNights(
      (nightPrices) =>
        nightPrices.originalSurcharges?.extra_guest_surcharge_sell_amount ??
        nightPrices.surcharges?.extra_guest_surcharge_sell_amount ??
        0,
      (disabledType) => disabledType?.extra_guest_surcharge ?? false,
    );

    if (hasAvailableExtraGuest) {
      types.push(EXTRA_GUEST_TYPE);
    }

    // Check revenue management availability
    const hasAvailableRevenueManagement = hasRefundableNights(
      (nightPrices) =>
        nightPrices.originalSurcharges?.surcharge_internal_sell_amount ??
        nightPrices.surcharges?.surcharge_internal_sell_amount ??
        0,
      (disabledType) => disabledType?.internal_surcharge ?? false,
    );

    if (hasAvailableRevenueManagement) {
      types.push(REVENUE_MANAGEMENT_TYPE);
    }

    return types;
  }, [dateRange, disabledRefundTypes, refundAmountsPerNight]);

  // Update selectedSurchargeTypes to only include available types when data loads
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (availableSurchargeTypes.length > 0 && !hasInitialized) {
      // This is the initial load - set up default selection
      if (selectedSurchargeTypes.length === 0) {
        // No initial selection, select all available types
        onSelectedSurchargeTypesChange(availableSurchargeTypes);
      } else {
        // Filter current selection to only include available types
        const filteredTypes = selectedSurchargeTypes.filter((type) => availableSurchargeTypes.includes(type));

        if (filteredTypes.length !== selectedSurchargeTypes.length) {
          // Update if some types were filtered out
          onSelectedSurchargeTypesChange(filteredTypes);
        }
      }
      setHasInitialized(true);
    }
  }, [availableSurchargeTypes, selectedSurchargeTypes, onSelectedSurchargeTypesChange, hasInitialized]);

  // Define surcharge type options - now using dynamic list
  const surchargeTypeOptions = availableSurchargeTypes;

  // Helper function to calculate refunds with specific surcharge types
  const calculateRefundAmountsWithTypes = useCallback(
    (nights: Set<string>, surchargeTypes: Array<string>) => {
      const selectedPrices = Array.from(nights).map((date) => refundAmountsPerNight[date]);

      let packageRefund = 0;
      let surchargeRefund = 0;

      selectedPrices.forEach((prices) => {
        // Always include base package if selected
        if (surchargeTypes.includes(BASE_PACKAGE_TYPE)) {
          packageRefund += prices.packagePrice;
        }

        // Include surcharges based on selection
        if (prices.surcharges) {
          if (surchargeTypes.includes(PEAK_PERIOD_TYPE)) {
            surchargeRefund += prices.surcharges.peak_period_surcharge_sell_amount || 0;
          }
          if (surchargeTypes.includes(EXTRA_GUEST_TYPE)) {
            surchargeRefund += prices.surcharges.extra_guest_surcharge_sell_amount || 0;
          }
          if (surchargeTypes.includes(REVENUE_MANAGEMENT_TYPE)) {
            surchargeRefund += prices.surcharges.surcharge_internal_sell_amount || 0;
          }
        }
      });

      // Nightly rate (base package price) refund at maximum allowed
      packageRefund = Math.min(packageRefund, maxRefundAmount);

      // Surcharge refund at maximum allowed
      surchargeRefund = Math.min(surchargeRefund, maxSurchargeRefundAmount);

      return { packageRefund, surchargeRefund };
    },
    [refundAmountsPerNight, maxRefundAmount, maxSurchargeRefundAmount],
  );

  const handleSurchargeTypesChange = (event: SelectChangeEvent<typeof selectedSurchargeTypes>) => {
    const {
      target: { value },
    } = event;
    const newTypes = typeof value === 'string' ? value.split(',') : value;
    onSelectedSurchargeTypesChange(newTypes);

    // Calculate refunds with the NEW surcharge types immediately
    const { packageRefund, surchargeRefund } = calculateRefundAmountsWithTypes(selectedNightsState, newTypes);
    setRefundToCustomer(packageRefund);
    setSurchargeRefundedToCustomer(surchargeRefund);

    // Update form data to include selected surcharge types and calculated values
    const formData = new FormData(formRef.current);
    formData.set('selectedSurchargeTypes', JSON.stringify(newTypes));
    formData.set('totalRefundedToCustomer', packageRefund.toString());
    formData.set('surchargeRefundedToCustomer', surchargeRefund.toString());

    handleLateChangeOfMindFieldChange(formData);
  };

  // Recalculate refunds when selectedSurchargeTypes or selectedNights changes
  useEffect(() => {
    const { packageRefund, surchargeRefund } = calculateRefundAmounts(selectedNightsState);
    setRefundToCustomer(packageRefund);
    setSurchargeRefundedToCustomer(surchargeRefund);
  }, [selectedSurchargeTypes, selectedNightsState, calculateRefundAmounts, setSurchargeRefundedToCustomer]);

  const handleNightSelection = (date, shouldSelect) => {
    const formData = new FormData(formRef.current);
    const arrCopy = new Set(selectedNightsState);
    const dateString = date.toISOString();

    if (shouldSelect) {
      arrCopy.add(dateString);
    } else {
      // Remove date from selectedNights if checkbox is unchecked
      arrCopy.delete(dateString);
    }

    const { packageRefund, surchargeRefund } = calculateRefundAmounts(arrCopy);

    selectNights(arrCopy);
    setRefundToCustomer(packageRefund);
    setSurchargeRefundedToCustomer(surchargeRefund);

    // Update form data
    formData.set('totalRefundedToCustomer', packageRefund.toString());
    formData.set('numberOfNights', arrCopy.size.toString());
    formData.set('surchargeRefundedToCustomer', surchargeRefund.toString());

    handleLateChangeOfMindFieldChange(formData, arrCopy);
  };

  const handleCheckboxChange = (date) => (event) => {
    handleNightSelection(date, event.target.checked);
  };

  const handleRowClick = (date) => (event) => {
    // Prevent row click when clicking directly on checkbox
    if (event.target.type === 'checkbox') {
      return;
    }

    const dateString = date.toISOString();
    const disabledTypes = disabledRefundTypes[dateString];

    // Don't allow clicking on fully disabled rows
    if (disabledTypes?.isDisabled) {
      return;
    }

    // Check if we can still select based on available surcharge types
    const canSelectAnyType = hasAvailableSurchargeTypes(selectedSurchargeTypes, disabledTypes);

    if (!canSelectAnyType) {
      return;
    }

    const isCurrentlyChecked = selectedNightsState.has(dateString);
    handleNightSelection(date, !isCurrentlyChecked);
  };

  const handleTotalRefundedToCustomerChange = useCallback(
    (e) => {
      const formData = new FormData(formRef.current);
      const limitedRefundAmount = Math.min(e.target.value, maxRefundAmount);
      setRefundToCustomer(limitedRefundAmount);
      // TODO: Add surcharge refund recalculation as separate input
      // Recalculate surcharge refund based on new package refund
      const { surchargeRefund } = calculateRefundAmounts(selectedNightsState);
      setSurchargeRefundedToCustomer(surchargeRefund);

      formData.set('totalRefundedToCustomer', limitedRefundAmount.toFixed(2));
      formData.set('surchargeRefundedToCustomer', surchargeRefund.toFixed(2));
      handleLateChangeOfMindFieldChange(formData);
    },
    [
      formRef,
      maxRefundAmount,
      calculateRefundAmounts,
      selectedNightsState,
      setSurchargeRefundedToCustomer,
      handleLateChangeOfMindFieldChange,
    ],
  );

  const handleSelectAllNights = () => {
    const formData = new FormData(formRef.current);
    // Only select nights that have at least one available refund type
    const availableNights = dateRange.filter((date) => {
      const dateString = date.toISOString();
      const disabledTypes = disabledRefundTypes[dateString];

      if (!disabledTypes) {
        return true;
      } // No restrictions
      if (disabledTypes.isDisabled) {
        return false;
      } // All types disabled

      // Check if any selected surcharge type is available
      return hasAvailableSurchargeTypes(selectedSurchargeTypes, disabledTypes);
    });

    const arrCopy = new Set(availableNights.map((date) => date.toISOString()));
    const { packageRefund, surchargeRefund } = calculateRefundAmounts(arrCopy);

    selectNights(arrCopy);
    setRefundToCustomer(packageRefund);
    setSurchargeRefundedToCustomer(surchargeRefund);

    formData.set('totalRefundedToCustomer', packageRefund.toString());
    formData.set('numberOfNights', arrCopy.size.toString());
    formData.set('surchargeRefundedToCustomer', surchargeRefund.toString());

    handleLateChangeOfMindFieldChange(formData, arrCopy);
  };

  const handleSelectNoNights = () => {
    const formData = new FormData(formRef.current);
    const arrCopy = new Set<string>();
    const { packageRefund, surchargeRefund } = calculateRefundAmounts(arrCopy);

    selectNights(arrCopy);
    setRefundToCustomer(packageRefund);
    setSurchargeRefundedToCustomer(surchargeRefund);

    formData.set('totalRefundedToCustomer', packageRefund.toString());
    formData.set('numberOfNights', arrCopy.size.toString());
    formData.set('surchargeRefundedToCustomer', surchargeRefund.toString());

    handleLateChangeOfMindFieldChange(formData, arrCopy);
  };

  const handleAllNightsChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      handleSelectAllNights();
    } else {
      handleSelectNoNights();
    }
  };

  // Calculate available nights for "select all" checkbox state
  const availableNightsForSelectAll = dateRange.filter((date) => {
    const dateString = date.toISOString();
    const disabledTypes = disabledRefundTypes[dateString];

    if (!disabledTypes) {
      return true;
    } // No restrictions
    if (disabledTypes.isDisabled) {
      return false;
    } // All types disabled

    // Check if any selected surcharge type is available
    return hasAvailableSurchargeTypes(selectedSurchargeTypes, disabledTypes);
  });

  const selectedAvailableNightsForSelectAll = Array.from(selectedNightsState).filter((dateKey) => {
    const disabledTypes = disabledRefundTypes[dateKey];

    if (!disabledTypes) {
      return true;
    } // No restrictions
    if (disabledTypes.isDisabled) {
      return false;
    } // All types disabled

    // Check if any selected surcharge type is available
    return hasAvailableSurchargeTypes(selectedSurchargeTypes, disabledTypes);
  });

  const isAllNightsSelected =
    selectedAvailableNightsForSelectAll.length === availableNightsForSelectAll.length &&
    availableNightsForSelectAll.length > 0;
  const isSomeNightsSelected =
    selectedAvailableNightsForSelectAll.length > 0 &&
    selectedAvailableNightsForSelectAll.length < availableNightsForSelectAll.length;

  // Precompute summary totals
  const summaryTotals = useMemo(() => {
    const allPrices = dateRange.map((date) => refundAmountsPerNight[date.toISOString()]).filter(Boolean);
    const totalPackagePrice = sum(allPrices.map((prices) => prices.originalPackagePrice ?? prices.packagePrice));
    const totalPeakPeriod = sum(
      allPrices.map(
        (prices) =>
          (prices.originalSurcharges?.peak_period_surcharge_sell_amount ??
            prices.surcharges?.peak_period_surcharge_sell_amount) ||
          0,
      ),
    );
    const totalExtraGuest = sum(
      allPrices.map(
        (prices) =>
          (prices.originalSurcharges?.extra_guest_surcharge_sell_amount ??
            prices.surcharges?.extra_guest_surcharge_sell_amount) ||
          0,
      ),
    );
    const totalRevenueManagement = sum(
      allPrices.map(
        (prices) =>
          (prices.originalSurcharges?.surcharge_internal_sell_amount ??
            prices.surcharges?.surcharge_internal_sell_amount) ||
          0,
      ),
    );

    return {
      totalPackagePrice,
      totalPeakPeriod,
      totalExtraGuest,
      totalRevenueManagement,
    };
  }, [dateRange, refundAmountsPerNight]);

  return (
    <Box mt={3}>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell padding="checkbox" sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText' }}>
                <Checkbox
                  checked={isAllNightsSelected}
                  indeterminate={isSomeNightsSelected}
                  size="small"
                  onChange={handleAllNightsChange}
                  sx={{
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&.Mui-checked': {
                      color: 'primary.contrastText',
                    },
                    '&.MuiCheckbox-indeterminate': {
                      color: 'primary.contrastText',
                    },
                  }}
                />
              </TableCell>
              <TableCell sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText' }}>Date</TableCell>
              <TableCell align="right" sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText' }}>
                Nightly Rate
              </TableCell>
              {!!availableSurcharges.peak_period_surcharge_sell_amount && (
                <TableCell align="right" sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText' }}>
                  Peak Period
                </TableCell>
              )}
              {!!availableSurcharges.extra_guest_surcharge_sell_amount && (
                <TableCell align="right" sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText' }}>
                  Extra Guest
                </TableCell>
              )}
              {!!availableSurcharges.surcharge_internal_sell_amount && (
                <TableCell align="right" sx={{ backgroundColor: 'primary.main', color: 'primary.contrastText' }}>
                  Rev Mgmt
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {dateRange.map((date, index) => {
              const dateKey = date.toISOString();
              const nightPrices = refundAmountsPerNight[dateKey];
              const isChecked = selectedNightsState.has(dateKey);
              const disabledTypes = disabledRefundTypes[dateKey];
              const isDisabled = disabledTypes?.isDisabled;

              // Check if the night can be selected based on current surcharge type selection
              const canSelectNight =
                !disabledTypes || hasAvailableSurchargeTypes(selectedSurchargeTypes, disabledTypes);

              // Individual column disabled states
              const isBasePackageDisabled =
                !isSurchargeTypeAvailable(BASE_PACKAGE_TYPE, disabledTypes) ||
                !selectedSurchargeTypes.includes(BASE_PACKAGE_TYPE);
              const isPeakPeriodDisabled =
                !isSurchargeTypeAvailable(PEAK_PERIOD_TYPE, disabledTypes) ||
                !selectedSurchargeTypes.includes(PEAK_PERIOD_TYPE);
              const isExtraGuestDisabled =
                !isSurchargeTypeAvailable(EXTRA_GUEST_TYPE, disabledTypes) ||
                !selectedSurchargeTypes.includes(EXTRA_GUEST_TYPE);
              const isRevenueMgmtDisabled =
                !isSurchargeTypeAvailable(REVENUE_MANAGEMENT_TYPE, disabledTypes) ||
                !selectedSurchargeTypes.includes(REVENUE_MANAGEMENT_TYPE);

              // Precompute nightly rate formatted value to avoid inline IIFE in JSX
              const rawAmount = nightPrices?.originalPackagePrice ?? nightPrices?.packagePrice;
              const nightlyRateValue = Number.isFinite(Number(rawAmount)) ? Number(rawAmount) : fallbackNightlyPrice;
              const nightlyRateFormatted = currencyFormatter(currencyCode, nightlyRateValue, 2);

              return (
                <TableRow
                  key={dateKey}
                  hover={canSelectNight}
                  selected={isChecked}
                  onClick={canSelectNight ? handleRowClick(date) : undefined}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    cursor: canSelectNight ? 'pointer' : 'not-allowed',
                    opacity: isDisabled ? 0.5 : 1,
                    backgroundColor: isDisabled ? 'grey.200' : 'inherit',
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      data-cy={`date-range-${index}`}
                      checked={isChecked}
                      disabled={!canSelectNight}
                      onChange={canSelectNight ? handleCheckboxChange(date) : undefined}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>
                  <TableCell component="th" scope="row" sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" color={isDisabled ? 'text.disabled' : 'text.primary'}>
                      {formatDateWeekDayShortMonth(date)}
                    </Typography>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      opacity: isBasePackageDisabled ? 0.5 : 1,
                      backgroundColor: disabledTypes?.nightly_rate ? 'grey.300' : 'inherit',
                    }}
                  >
                    {loading === 'loading' ? (
                      <Spinner size={15} inline />
                    ) : loading === 'success' ? (
                      <Typography
                        variant="body2"
                        color={isBasePackageDisabled ? 'text.disabled' : 'text.primary'}
                        sx={{ textDecoration: disabledTypes?.nightly_rate ? 'line-through' : 'none' }}
                      >
                        {nightlyRateFormatted}
                      </Typography>
                    ) : (
                      <Typography variant="body2">-</Typography>
                    )}
                  </TableCell>
                  {!!availableSurcharges.peak_period_surcharge_sell_amount && (
                    <TableCell
                      align="right"
                      sx={{
                        opacity: isPeakPeriodDisabled ? 0.5 : 1,
                        backgroundColor: disabledTypes?.peak_period_surcharge ? 'grey.300' : 'inherit',
                      }}
                    >
                      {loading === 'success' &&
                      (nightPrices?.originalSurcharges?.peak_period_surcharge_sell_amount ||
                        nightPrices?.surcharges?.peak_period_surcharge_sell_amount) > 0 ? (
                        <Typography
                          variant="body2"
                          color={isPeakPeriodDisabled ? 'text.disabled' : 'text.primary'}
                          sx={{ textDecoration: disabledTypes?.peak_period_surcharge ? 'line-through' : 'none' }}
                        >
                          {currencyFormatter(
                            currencyCode,
                            nightPrices?.originalSurcharges?.peak_period_surcharge_sell_amount ??
                              nightPrices?.surcharges?.peak_period_surcharge_sell_amount,
                            2,
                          )}
                        </Typography>
                      ) : loading === 'loading' ? (
                        <Spinner size={15} inline />
                      ) : (
                        <Typography variant="body2" color={isPeakPeriodDisabled ? 'text.disabled' : 'text.secondary'}>
                          -
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  {!!availableSurcharges.extra_guest_surcharge_sell_amount && (
                    <TableCell
                      align="right"
                      sx={{
                        opacity: isExtraGuestDisabled ? 0.5 : 1,
                        backgroundColor: disabledTypes?.extra_guest_surcharge ? 'grey.300' : 'inherit',
                      }}
                    >
                      {loading === 'success' &&
                      (nightPrices?.originalSurcharges?.extra_guest_surcharge_sell_amount ||
                        nightPrices?.surcharges?.extra_guest_surcharge_sell_amount) > 0 ? (
                        <Typography
                          variant="body2"
                          color={isExtraGuestDisabled ? 'text.disabled' : 'text.primary'}
                          sx={{ textDecoration: disabledTypes?.extra_guest_surcharge ? 'line-through' : 'none' }}
                        >
                          {currencyFormatter(
                            currencyCode,
                            nightPrices?.originalSurcharges?.extra_guest_surcharge_sell_amount ??
                              nightPrices?.surcharges?.extra_guest_surcharge_sell_amount,
                            2,
                          )}
                        </Typography>
                      ) : loading === 'loading' ? (
                        <Spinner size={15} inline />
                      ) : (
                        <Typography variant="body2" color={isExtraGuestDisabled ? 'text.disabled' : 'text.secondary'}>
                          -
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  {!!availableSurcharges.surcharge_internal_sell_amount && (
                    <TableCell
                      align="right"
                      sx={{
                        opacity: isRevenueMgmtDisabled ? 0.5 : 1,
                        backgroundColor: disabledTypes?.internal_surcharge ? 'grey.300' : 'inherit',
                      }}
                    >
                      {loading === 'success' &&
                      (nightPrices?.originalSurcharges?.surcharge_internal_sell_amount ||
                        nightPrices?.surcharges?.surcharge_internal_sell_amount) > 0 ? (
                        <Typography
                          variant="body2"
                          color={isRevenueMgmtDisabled ? 'text.disabled' : 'text.primary'}
                          sx={{ textDecoration: disabledTypes?.internal_surcharge ? 'line-through' : 'none' }}
                        >
                          {currencyFormatter(
                            currencyCode,
                            nightPrices?.originalSurcharges?.surcharge_internal_sell_amount ??
                              nightPrices?.surcharges?.surcharge_internal_sell_amount,
                            2,
                          )}
                        </Typography>
                      ) : loading === 'loading' ? (
                        <Spinner size={15} inline />
                      ) : (
                        <Typography variant="body2" color={isRevenueMgmtDisabled ? 'text.disabled' : 'text.secondary'}>
                          -
                        </Typography>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}

            {/* Summary Row */}
            {summaryTotals && (
              <TableRow sx={{ backgroundColor: 'grey.100', '&:hover': { backgroundColor: 'grey.100' } }}>
                <TableCell padding="checkbox"></TableCell>
                <TableCell component="th" scope="row">
                  <Typography variant="body2" fontWeight="bold">
                    Total {dateRange.length} {pluralize('night', dateRange.length)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold">
                    {currencyFormatter(currencyCode, summaryTotals.totalPackagePrice, 2)}
                  </Typography>
                </TableCell>
                {!!summaryTotals.totalPeakPeriod && (
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      {summaryTotals.totalPeakPeriod > 0
                        ? currencyFormatter(currencyCode, summaryTotals.totalPeakPeriod, 2)
                        : '-'}
                    </Typography>
                  </TableCell>
                )}
                {!!summaryTotals.totalExtraGuest && (
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      {summaryTotals.totalExtraGuest > 0
                        ? currencyFormatter(currencyCode, summaryTotals.totalExtraGuest, 2)
                        : '-'}
                    </Typography>
                  </TableCell>
                )}
                {!!summaryTotals.totalRevenueManagement && (
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      {summaryTotals.totalRevenueManagement > 0
                        ? currencyFormatter(currencyCode, summaryTotals.totalRevenueManagement, 2)
                        : '-'}
                    </Typography>
                  </TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Surcharge Types Multi-Select */}
      <Box mt={3}>
        <FormControl fullWidth size="small">
          <InputLabel id="surcharge-types-label">Choose Refund Components</InputLabel>
          <Select<Array<string>>
            labelId="surcharge-types-label"
            id="surcharge-types-select"
            multiple
            value={selectedSurchargeTypes}
            onChange={handleSurchargeTypesChange}
            input={<OutlinedInput id="select-multiple-chip" label="Choose Refund Components" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {surchargeTypeOptions.map((type) => (
              <MenuItem key={type} value={type}>
                <Checkbox checked={selectedSurchargeTypes.includes(type)} />
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Hidden input to hold the selected surcharge types */}
        <input type="hidden" name="selectedSurchargeTypes" value={JSON.stringify(selectedSurchargeTypes)} />
      </Box>

      <Grid container sx={{ mt: 3 }}>
        <Grid sm={4} alignContent="center" pl={2}>
          <Typography fontWeight="bold">
            {selectedNightsState.size} / {dateRange.length} {dateRange.length === 1 ? 'night' : 'nights'} selected
          </Typography>
          {/* hidden input to hold the shouldSurchargeBeRefunded value */}
          <input type="hidden" name="shouldSurchargeBeRefunded" value="true" />
          {/* Hidden input to hold the numberOfNights tab value */}
          <input type="hidden" name="numberOfNights" value={selectedNightsState.size} />
        </Grid>
        <Grid sm={4} p={2}>
          <Stack>
            <Typography fontWeight="bold">Package Refund</Typography>
            <NumberFormatCustom
              value={refundToCustomer}
              onChange={handleTotalRefundedToCustomerChange}
              size="small"
              data-cy="RefundToCustomerAmount"
              prefix={getCurrencySymbol(currencyCode)}
              disabled={disableCustomAmount}
            />
            <Typography variant="caption" color="text.secondary">
              Max: {currencyFormatter(currencyCode, maxRefundAmount, 2)}
            </Typography>
            <input type="hidden" name="totalRefundedToCustomer" value={refundToCustomer} />
          </Stack>
        </Grid>
        <Grid sm={4} p={2}>
          <Stack>
            <Typography fontWeight="bold">Surcharge Refund</Typography>
            <NumberFormatCustom
              onChange={noop}
              value={surchargeRefundedToCustomer}
              disabled
              size="small"
              data-cy="surchargeRefundToCustomerAmount"
              prefix={getCurrencySymbol(currencyCode)}
            />
            <Typography variant="caption" color="text.secondary">
              Max: {currencyFormatter(currencyCode, maxSurchargeRefundAmount, 2)}
            </Typography>
            <input type="hidden" name="surchargeRefundedToCustomer" value={surchargeRefundedToCustomer} />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
